import React, { useState, useMemo, Suspense, Activity, lazy } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ScrollRestoration } from 'react-router-dom';

// Lazy-load Feed for the Activity-wrapped instance
const Feed = lazy(() => import('../Feed'));
import { signIn, auth, signOut as firebaseSignOut } from '../../services/firebase';
import { syncActivities } from '../../services/strava';
import { useToast } from '../common/ToastContext';

// Hooks
import { useAuthState } from '../../hooks/useAuthState';
import { useAppData } from '../../hooks/useAppData';
import { useStravaOAuth } from '../../hooks/useStravaOAuth';
import { useNewsNotifications } from '../../hooks/useNewsNotifications';
import { usePostHogIdentify } from '../../services/posthog';

// Core components
import { LoadingScreen } from '../common/LoadingScreen';
import { StravaConnectingScreen } from '../onboarding/StravaConnectingScreen';
import { LoginScreen } from '../auth/LoginScreen';
import OnboardingFlow from '../onboarding/OnboardingFlow';
import DesktopHeader from '../navigation/DesktopHeader';
import MobileNav from '../navigation/MobileNav';
import MobileHeader from '../navigation/MobileHeader';
import { NavigationProgress } from '../common/NavigationProgress';
import { RouteSkeleton } from '../common/RouteSkeleton';
import { FeedProvider } from '../feed/FeedContext';
import { NewsProvider } from '../newsfeed/NewsContext';

export const RootLayout: React.FC = () => {
  const location = useLocation();
  const toast = useToast();
  const [isSyncing, setIsSyncing] = useState(false);

  // Custom hooks for data and state management
  const { users, teams, systemSettings, usersLoaded } = useAppData();
  const { firebaseUser, currentUser, loading } = useAuthState(users, usersLoaded);
  const { isConnecting, progress } = useStravaOAuth(currentUser, toast);
  const { unreadNewsCount } = useNewsNotifications(currentUser, location.pathname);

  // Identify user in PostHog
  usePostHogIdentify(currentUser);

  // Memoize suspended user IDs (moved up to avoid hook order issues)
  const suspendedUserIds = useMemo(() => users.filter(u => u.isSuspended).map(u => u.id), [users]);

  // Handlers
  const handleStravaSync = async () => {
    setIsSyncing(true);
    try {
      const result: any = await syncActivities();
      toast.success(`Đồng bộ thành công! Đã cập nhật ${result.count || 0} hoạt động.`);
    } catch (error) {
      console.error('Sync failed', error);
      toast.error('Đồng bộ thất bại. Vui lòng thử lại sau.');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleLogin = async () => {
    try {
      await signIn();
    } catch (error: any) {
      console.error('Login failed', error);

      // Sign out the user to ensure they return to login screen
      try {
        await firebaseSignOut();
      } catch (signOutError) {
        console.error('Sign out failed', signOutError);
      }

      // Extract error message from the error object
      const errorMessage = error?.message || 'Đăng nhập thất bại.';
      toast.error(errorMessage);
    }
  };


  // Auto-logout suspended users
  React.useEffect(() => {
    if (currentUser?.isSuspended) {
      toast.error('Tài khoản của bạn đã bị tạm khóa. Vui lòng liên hệ quản trị viên.');
      firebaseSignOut().catch(console.error);
    }
  }, [currentUser, toast]);

  // Memoized filtered users for performance
  const filteredUsers = useMemo(() => {
    const { eventStartDate, eventEndDate } = systemSettings;
    if (!eventStartDate && !eventEndDate) return users;

    return users.map(user => {
      const filtered = user.activities.filter(act => {
        if (act.isDeleted) return false;
        const actDate = new Date(act.date);

        if (eventStartDate) {
          const start = new Date(eventStartDate);
          start.setHours(0, 0, 0, 0);
          if (actDate < start) return false;
        }

        if (eventEndDate) {
          const end = new Date(eventEndDate);
          end.setHours(23, 59, 59, 999);
          if (actDate > end) return false;
        }

        return true;
      });

      const totalPoints = filtered.reduce((sum, act) => sum + (act.points || 0), 0);
      return { ...user, activities: filtered, points: totalPoints };
    });
  }, [users, systemSettings]);

  // Used by child routes via Outlet context if needed, or we can just pass down via context
  // But since Router doesn't pass props to Outlet easily without `useOutletContext`,
  // and maintaining the exact existing architecture is simpler if we just export this
  // or keep using local logic.
  // For now, let's keep the layout rendering structure.

  // Loading states
  if (loading || isConnecting) {
    return isConnecting ? (
      <StravaConnectingScreen progress={progress} />
    ) : (
      <LoadingScreen />
    );
  }

  // Handle state where user is authenticated but profile is not yet loaded/created
  if (firebaseUser && !currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 flex-col">
        <LoadingScreen message="Loading your profile..." />
      </div>
    );
  }

  // Login screen
  if (!currentUser) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  // Onboarding flow
  // In Data Router, we can handle this via a separate route or conditional rendering here.
  // To match existing App.tsx logic strictly:
  if (!currentUser.hasCompletedOnboarding) {
    // App.tsx rendered Routes for /onboarding here.
    // We can just render the Onboarding component directly for simplicity
    // or let the Router handle /onboarding if we structure it right.
    // Given the conditional return design of App.tsx, direct rendering is safest
    // to prevent accessing other routes.
    return <OnboardingFlow user={currentUser} />;
  }


  // ... (inside component)

  // Main app

  return (
    <FeedProvider systemSettings={systemSettings} currentUserId={currentUser.id} suspendedUserIds={suspendedUserIds}>
      <NewsProvider>
        {/* Outer container fixed to viewport height to prevent body scroll */}
        <div className="h-[100dvh] w-full fixed inset-0 overflow-hidden text-slate-800 font-sans selection:bg-indigo-200 selection:text-indigo-900 flex flex-col bg-slate-50">
          <ScrollRestoration />
          <NavigationProgress />

          {/* Animated Background */}
          <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
            {/* Blob 1 (Top Left): Deep Purple/Blue */}
            <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] bg-gradient-to-br from-blue-400 to-purple-500 rounded-full blur-3xl opacity-60 animate-float-slow mix-blend-multiply"></div>

            {/* Blob 2 (Bottom Right): Hot Pink/Orange */}
            <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[60%] bg-gradient-to-br from-pink-400 to-orange-400 rounded-full blur-3xl opacity-60 animate-float-medium mix-blend-multiply"></div>

            {/* Blob 3 (Center): Teal/Cyan */}
            <div className="absolute top-[30%] left-[20%] w-[50%] h-[50%] bg-gradient-to-br from-teal-300 to-cyan-400 rounded-full blur-3xl opacity-50 animate-float-fast mix-blend-multiply"></div>
          </div>

          {/* Desktop Header - fixed height handled by flex layout */}
          <div className="shrink-0 z-50">
            <DesktopHeader
              user={currentUser}
              unreadNewsCount={unreadNewsCount}
            />
          </div>

          {/* Mobile Header - fixed height handled by flex layout */}
          <div className="shrink-0 z-50">
            <MobileHeader
              user={currentUser}
            />
          </div>

          {/* Main Content Area - Forces internal scrolling */}
          <main className="flex-1 relative w-full overflow-hidden">
            <Suspense fallback={<RouteSkeleton />}>
              {/* Feed wrapped in Activity with its own scroll container */}
              <Activity mode={location.pathname === '/feed' ? 'visible' : 'hidden'}>
                <div className="absolute inset-0 overflow-y-auto overflow-x-hidden scroll-smooth pb-20 md:pb-0">
                  <div className="w-full md:max-w-7xl md:mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <Feed users={filteredUsers} currentUser={currentUser} />
                  </div>
                </div>
              </Activity>

              {/* Other routes render via Outlet when not on /feed */}
              {location.pathname !== '/feed' && (
                <div className="absolute inset-0 overflow-y-auto overflow-x-hidden scroll-smooth pb-20 md:pb-0">
                  <div className="w-full md:max-w-7xl md:mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <Outlet context={{
                      currentUser,
                      users: filteredUsers,
                      teams,
                      systemSettings,
                      isSyncing,
                      onSync: handleStravaSync,
                      isAdmin: !!currentUser.isAdmin
                    }} />
                  </div>
                </div>
              )}
            </Suspense>
          </main>

          {/* Mobile Bottom Navigation - Absolute/Fixed on top of everything at bottom */}
          <MobileNav
            isAdmin={!!currentUser.isAdmin}
            unreadNewsCount={unreadNewsCount}
          />
        </div>
      </NewsProvider>
    </FeedProvider>
  );
};
